import { useState } from "react";
import { X, Plus, Trash2, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import {
  useCreateDatabankCategoryMutation,
  useDeleteDatabankCategoryMutation,
  useCreateDatabankSubCategoryMutation,
  useDeleteDatabankSubCategoryMutation,
} from "../api/databankApi";

function err(e: any) {
  return e?.data?.message || e?.data?.errors?.[Object.keys(e?.data?.errors ?? {})[0]]?.[0] || "Something went wrong";
}

export default function CategoriesManager({ categories, onClose }: { categories: any[]; onClose: () => void }) {
  const [createCat, { isLoading: creating }] = useCreateDatabankCategoryMutation();
  const [deleteCat] = useDeleteDatabankCategoryMutation();
  const [createSub] = useCreateDatabankSubCategoryMutation();
  const [deleteSub] = useDeleteDatabankSubCategoryMutation();

  const [newCat, setNewCat] = useState("");
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [subInput, setSubInput] = useState<Record<number, string>>({});
  const [error, setError] = useState("");

  const addCategory = async () => {
    setError("");
    if (!newCat.trim()) return;
    try { await createCat({ name: newCat.trim() }).unwrap(); setNewCat(""); }
    catch (e) { setError(err(e)); }
  };

  const addSub = async (categoryId: number) => {
    setError("");
    const name = (subInput[categoryId] ?? "").trim();
    if (!name) return;
    try { await createSub({ categoryId, name }).unwrap(); setSubInput((p) => ({ ...p, [categoryId]: "" })); }
    catch (e) { setError(err(e)); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-900">Manage Categories</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-500" /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {/* Add category */}
          <div className="flex gap-2">
            <input
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCategory()}
              placeholder="New category name…"
              className="flex-1 border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
            />
            <button onClick={addCategory} disabled={creating} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
            </button>
          </div>

          {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>}

          {/* Category list */}
          <div className="divide-y border rounded-lg">
            {categories.length === 0 && <div className="p-4 text-sm text-gray-400 text-center">No categories yet.</div>}
            {categories.map((c) => {
              const open = expanded[c.id];
              const subs = c.sub_categories ?? [];
              return (
                <div key={c.id}>
                  <div className="flex items-center gap-2 px-3 py-2">
                    <button onClick={() => setExpanded((p) => ({ ...p, [c.id]: !open }))} className="text-gray-400">
                      {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <span className="flex-1 text-sm font-semibold text-gray-800">{c.name}</span>
                    <span className="text-[10px] text-gray-400">{subs.length} sub</span>
                    <button
                      onClick={() => { if (window.confirm(`Delete "${c.name}" and its sub-categories? Items using it will lose their category.`)) deleteCat(c.id); }}
                      className="p-1 rounded hover:bg-rose-50 text-rose-500"
                    ><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>

                  {open && (
                    <div className="pl-9 pr-3 pb-3 space-y-1.5">
                      {subs.map((s: any) => (
                        <div key={s.id} className="flex items-center gap-2">
                          <span className="flex-1 text-sm text-gray-600">{s.name}</span>
                          <button onClick={() => deleteSub(s.id)} className="p-1 rounded hover:bg-rose-50 text-rose-400"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <div className="flex gap-2 pt-1">
                        <input
                          value={subInput[c.id] ?? ""}
                          onChange={(e) => setSubInput((p) => ({ ...p, [c.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === "Enter" && addSub(c.id)}
                          placeholder="New sub-category…"
                          className="flex-1 border rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                        />
                        <button onClick={() => addSub(c.id)} className="px-3 py-1.5 rounded-lg border text-sm hover:bg-gray-50">Add</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-6 py-4 border-t">
          <button onClick={onClose} className="w-full py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800">Done</button>
        </div>
      </div>
    </div>
  );
}
